import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { getCurrentToken } from "../msal/tokenFetcher";
import { msalConfig, loginRequest } from "../msal/authConfig";
import { jwtDecode } from "jwt-decode";
import {
  fetchCustomers,
  fetchUsers,
  fetchRolesFromUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchUserByEmail,
  fetchCustomerIdByTenantId,
  fetchCustomerById,
} from '../../settingssidebar/user/userApi';

export const msalInstance = new PublicClientApplication(msalConfig);

let msalInitialized = false;

export async function initializeMsal() {
  if (msalInitialized) return;
  console.log("=> Initializing MSAL...");
  await msalInstance.initialize();
  msalInitialized = true;
  console.log("=> MSAL Initialized");

  msalInstance.addEventCallback(async (event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
      const authToken = await getCurrentToken(msalInstance);
      if (authToken) {
        localStorage.setItem("authToken", authToken);
      }
    }
  });
}

export async function clearAllSessions() {
  console.log("=> Clearing all authentication sessions");

  try {
    await initializeMsal(); // Ensure MSAL is ready

    // Clear MSAL accounts and token cache
    const accounts = msalInstance.getAllAccounts();
    console.log(`=> Found ${accounts.length} MSAL accounts to clear`);

    // Remove all accounts from MSAL
    for (const account of accounts) {
      try {
        await msalInstance.getTokenCache().removeAccount(account);
        console.log(`=> Removed MSAL account: ${account.username}`);
      } catch (error) {
        console.warn(`=> Failed to remove account ${account.username}:`, error);
      }
    }

    // Clear MSAL active account
    msalInstance.setActiveAccount(null);

    // Clear all authentication-related localStorage keys
    const authKeys = [
      "authToken",
      "authUser",
      "username",
      "isSuperAdmin",
      "isValidUser",
      "isAdmin" // Added isAdmin to clear
    ];

    // Clear application-specific localStorage keys (optional - for clean slate)
    const appKeys = [
      "selectedFile",
      "selectedInstance",
      "integrationType",
      "uploadedFiles",
      "fileDataMap"
    ];

    // Remove authentication keys
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`=> Removed ${key} from localStorage`);
      }
    });

    // Optionally remove application-specific keys for a clean slate
    appKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`=> Removed application key ${key} from localStorage`);
      }
    });

    // Clear sessionStorage as well (in case anything is stored there)
    try {
      sessionStorage.clear();
      console.log("=> Cleared sessionStorage");
    } catch (error) {
      console.warn("=> Could not clear sessionStorage:", error);
    }

    // Clear any cookies that might be related to authentication
    try {
      // Clear MSAL related cookies if any
      document.cookie.split(";").forEach(function (c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      console.log("=> Cleared cookies");
    } catch (error) {
      console.warn("=> Could not clear cookies:", error);
    }

    console.log("=> All sessions and data cleared successfully");

  } catch (error) {
    console.error("=> Error during clearAllSessions:", error);

    // Fallback: at least clear localStorage even if MSAL operations fail
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("username");
      localStorage.removeItem("isSuperAdmin");
      localStorage.removeItem("isValidUser");
      localStorage.removeItem("isAdmin");
      console.log("=> Fallback: Cleared localStorage");
    } catch (fallbackError) {
      console.error("=> Fallback also failed:", fallbackError);
    }
  }
}

export async function getToken() {
  await initializeMsal();
  const authToken = await getCurrentToken(msalInstance);
  if (authToken) {
    localStorage.setItem("authToken", authToken);
  }
  return authToken;
}

export const handleLogin = async (loginType = "popup", navigate, login, email) => {

  try {
    await initializeMsal();
    let emailId;
    let isValidUser = false;
    let isSuperAdmin = false;
    let isAdmin = false;

    // Create a custom login request with email hint if provided
    const customLoginRequest = {
      ...loginRequest,
      ...(email && email.trim() && { loginHint: email.trim() })
    };

    const loginResponse = await msalInstance.loginPopup(customLoginRequest);

    const authToken = await getCurrentToken(msalInstance);


    // roles from access token
    //const roles = decodedToken?.roles || [];

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
    const currentAccount = accounts?.length > 0 ? accounts[0] : null;

    if (currentAccount) {
      const { preferred_username: email, tid: t_id, oid: userId, name: userName } = currentAccount.idTokenClaims;
      emailId = email;
      const tempAuthUser = { token: authToken };
      const customerId = await fetchCustomerIdByTenantId(t_id, tempAuthUser);

      if (customerId) {
        const userResponse = await fetchUserByEmail(email, tempAuthUser);

        if (!userResponse || userResponse === "") {
          alert("User not found. Contact System admin.");
          return false;
        }

        if (!userResponse.isSuccess) {
          alert("User not found. Contact System admin.");
          return false;

        }

        debugger;
        const user = userResponse.user;
        const decoded = jwtDecode(authToken);
        // normalize once (case-insensitive, defensively handle missing claim)
        const roleSet = new Set((decoded?.roles ?? []).map(r => String(r).toLowerCase()));

        // exact role checks
        const isSuperAdmin = roleSet.has("superadmin");
        const isAdmin = roleSet.has("admin");
        const isUser = roleSet.has("user");
        isValidUser = true;

        if (!isValidUser) {
          alert("Contact System admin to make it active");
          return false;
        }

        const customerResponse = await fetchCustomerById(customerId, tempAuthUser);
        if (!customerResponse || !customerResponse.isSuccess) {
          alert("Unable to fetch customer information. Contact System admin.");
          return false;
        }

        const customer = customerResponse.customer;

        localStorage.setItem("username", email);
        localStorage.setItem("isSuperAdmin", isSuperAdmin);
        localStorage.setItem("isAdmin", isAdmin);
        localStorage.setItem("isValidUser", isValidUser);

        if (typeof navigate === "function") {
          login({
            username: emailId,
            firstName: user.firstName,
            lastName: user.lastName,
            userId: user.userId,
            customerId: user.customerId,
            customerName: customer.customerName,
            token: authToken,
            roleTitles: user.roleTitles,
            isSuperAdmin: isSuperAdmin,
            isAdmin: isAdmin,
            isActive: user.isActive
          });

          console.log('SSO Login successful:', {
            user,
            customer,
            isSuperAdmin
          });

          setTimeout(() => {
            navigate("/files");
          }, 100);
        }
      } else {
        alert("No customer found for your organization. Contact System admin.");
        return false;
      }
    }

    if (authToken) {
      localStorage.setItem("authToken", authToken);
      return true;
    }

    return false;
  } catch (e) {
    console.error("SSO login failed:", e);
    return false;
  }
};

export const handleLogout = async (logoutType = "redirect") => {
  await initializeMsal();

  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount) return;

  localStorage.removeItem("authToken");

  if (logoutType === "popup") {
    msalInstance.logoutPopup({ account: activeAccount }).catch((e) => {
      console.error(`logoutPopup failed: ${e}`);
    });
  } else {
    msalInstance.logoutRedirect({
      account: activeAccount,
      postLogoutRedirectUri: "/"
    }).catch((e) => {
      console.error(`logoutRedirect failed: ${e}`);
    });
  }
};
